'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DashboardContentWrapper from '../../../../../shared/components/dashboard-content-wrapper'
import Heading from '../../../../../shared/components/ui/typography/heading'
import { Button } from '@/shared/components/ui/button'
import Link from 'next/link'
import { useRegionAwareHref } from '@/shared/hooks/use-region-aware-href'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { BaseLayoutConfig } from '@/features/base/types/base-layout'
import { DEFAULT_BASE_LAYOUT_CONFIG } from '@/features/base/constants/default-layout-config'
import { BaseRegionResponse, unwrapBaseRegionResponse } from '@/features/base/types/region-response'
import { buildBaseLayoutContext } from '@/features/base/utils/context'
import { ensurePermanentArTab, resolveBaseLayoutConfig } from '@/features/base/utils/layout'
import { hydrateLayoutWithContext } from '@/features/base/utils/layout-hydration'
import { BaseLayoutEditor } from '@/features/base/components/admin/base-layout-editor'
import { RegionSettingsSection } from '@/features/base/components/admin/RegionSettingsSection'
import errorToast from '@/shared/lib/toast/error.toast'
import { BasePhoto, MissionStory, PrayerRequest } from '@/features/base/types/base-content'
import { PartnerRegionAssociation } from '@/features/partners/types/partner.type'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'
import { authFetchJson } from '@/shared/lib/auth-fetch'

function BaseAdminPage() {
  const { regionId, isLoading: isRegionLoading } = useRegionId()
  const { session } = useSession()
  const backHref = useRegionAwareHref('/base')
  const [regionData, setRegionData] = useState<BaseRegionResponse | null>(null)
  const [layoutConfig, setLayoutConfig] = useState<BaseLayoutConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isAuthorized = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

  const loadRegion = useCallback(async () => {
    if (!regionId) return

    try {
      setIsLoading(true)
      setError(null)
      const [regionRes, photosRes, storiesRes] = await Promise.all([
        authFetchJson<any>(`/regions/${regionId}`),
        authFetchJson<any>(`/base-photos/region/${regionId}`),
        authFetchJson<any>(`/mission-stories?regionId=${regionId}`),
      ])

      if (!regionRes.success) {
        throw new Error(regionRes.message || 'Unable to load region data')
      }
      if (!photosRes.success) {
        throw new Error(photosRes.message || 'Unable to load base photos')
      }
      if (!storiesRes.success) {
        throw new Error(storiesRes.message || 'Unable to load mission stories')
      }

      const region = unwrapBaseRegionResponse(regionRes)
      if (!region) {
        throw new Error('Region data not available')
      }

      const photosPayload = photosRes.data
      const parsedPhotos: BasePhoto[] = Array.isArray(photosPayload)
        ? photosPayload
        : Array.isArray(photosPayload?.items)
          ? photosPayload.items
          : []

      const storiesPayload = storiesRes.data
      const parsedStories: MissionStory[] = Array.isArray(storiesPayload)
        ? storiesPayload
        : Array.isArray(storiesPayload?.items)
          ? storiesPayload.items
          : []
      parsedStories.sort((a, b) => {
        const dateA = a.storyDate || a.createdAt || a.updatedAt || ''
        const dateB = b.storyDate || b.createdAt || b.updatedAt || ''
        return dateB.localeCompare(dateA)
      })

      const context = buildBaseLayoutContext(region, parsedPhotos, parsedStories, [], [])
      const resolved = ensurePermanentArTab(resolveBaseLayoutConfig(region.baseLayoutConfig))
      const hydrated = hydrateLayoutWithContext(resolved, context)

      setRegionData(region)
      setLayoutConfig(hydrated)
    } catch (error) {
      console.error('Failed to load base page data', error)
      const message = error instanceof Error ? error.message : 'Failed to load base page data'
      setError(message)
      errorToast(message)
      setRegionData(null)
      setLayoutConfig(null)
    } finally {
      setIsLoading(false)
    }
  }, [regionId])

  useEffect(() => {
    if (regionId) {
      loadRegion()
    }
  }, [regionId, loadRegion])

  const isBusy = isRegionLoading || isLoading

  const editorInitialLayout = useMemo(
    () => ensurePermanentArTab(layoutConfig ?? DEFAULT_BASE_LAYOUT_CONFIG),
    [layoutConfig]
  )

  return (
    <DashboardContentWrapper className="flex flex-col gap-6 pb-24 md:pb-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <Heading>Hub Page Editor</Heading>
          <p className="text-sm text-muted-foreground">
            Configure tabs, sections, and content for this region’s Base page.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link href={backHref}>Back to Hub Page</Link>
        </Button>
      </div>

      {!isAuthorized && (
        <div className="flex items-center justify-center rounded-md border border-destructive/40 bg-destructive/5 p-6 text-destructive">
          You do not have permission to edit the Base page.
        </div>
      )}

      {isAuthorized && (
        <>
          {isBusy && (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          )}

          {!isBusy && !regionId && (
            <div className="rounded-md border border-border p-6 text-center text-muted-foreground">
              Select a region to begin editing.
            </div>
          )}

          {!isBusy && regionId && error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-6 text-center text-destructive">
              {error}
            </div>
          )}

          {!isBusy && regionId && !error && layoutConfig && regionData && (
            <div className="space-y-6">
              <RegionSettingsSection
                region={regionData}
                regionId={regionId}
                onSaved={loadRegion}
              />
              <BaseLayoutEditor
                regionId={regionId}
                initialLayout={editorInitialLayout}
                onLayoutReload={loadRegion}
              />
            </div>
          )}
        </>
      )}
      <ScrollToTopFab />
    </DashboardContentWrapper>
  )
}

export default BaseAdminPage
